import { useEffect, useRef, memo } from 'react'
import PropTypes from 'prop-types'

// Internal context providers
import { withLazyImageContext } from '../../context/LazyImageContext'

// Internal components
import Loader from '../Loader/Loader'

const LazyImage = memo((props) => {
  const { image, alt, sizes, lazyLoad, modifiers } = props

  const modifierClasses = modifiers ? ' ' + modifiers.join(' ') : ''
  const backgroundStyle =
    image?.colorPalette && image?.colorPalette[0] ? { backgroundColor: image.colorPalette[0] } : false

  const loadedClass = 'is-loaded'
  const lazyStatusAttr = 'data-ll-status'

  // Refs
  const sourceRef = useRef()
  const imgSrcsetRef = useRef()
  const imageUrlRef = useRef()

  useEffect(() => {
    if (sourceRef.current && sourceRef.current.srcset.length && image?.optimizedWebPImageUrls) {
      const srcRef = sourceRef.current
      if (srcRef.srcset.length && getSrcset(image.optimizedWebPImageUrls) !== srcRef.srcset)
        srcRef.removeAttribute('srcset')
    }

    if (imgSrcsetRef.current && image?.optimizedImageUrls) {
      const imgRef = imgSrcsetRef.current
      if (imgRef.srcset.length && getSrcset(image.optimizedImageUrls) !== imgRef.srcset) {
        imgRef.removeAttribute('srcset')
        imgRef.classList.remove(loadedClass)
        imgRef.removeAttribute(lazyStatusAttr)
      }
    }

    if (imageUrlRef.current) {
      const urlRef = imageUrlRef.current
      if (urlRef.src.length && image.url !== urlRef.src) {
        urlRef.removeAttribute('src')
        urlRef.classList.remove(loadedClass)
        urlRef.removeAttribute(lazyStatusAttr)
      }
    }

    if (lazyLoad && image) lazyLoad.update()
  }, [image, lazyLoad])

  const getSrcset = (urlsObj) => {
    let srcSet = ''
    image.variantSourceWidths.map((width, index) => {
      srcSet += `${urlsObj[width] ? urlsObj[width] : urlsObj[index]} ${width}w, `
      return ''
    })
    return srcSet !== '' ? srcSet.slice(0, -2) : false
  }

  return (
    <picture className={`LazyImage${modifierClasses}`}>
      {image?.optimizedWebPImageUrls && (
        <source ref={sourceRef} data-srcset={getSrcset(image.optimizedWebPImageUrls)} sizes={sizes} type="image/webp" />
      )}
      {image?.optimizedImageUrls && (
        <img
          ref={imgSrcsetRef}
          data-srcset={getSrcset(image.optimizedImageUrls)}
          sizes={sizes}
          className="LazyImage-image"
          loading="lazy"
          alt={alt}
        />
      )}
      {image?.url && (
        <img
          ref={imageUrlRef}
          data-src={image.url}
          sizes={sizes}
          className="LazyImage-image"
          loading="lazy"
          alt={alt}
        />
      )}
      {backgroundStyle && (
        <div className="LazyImage-background" style={backgroundStyle}>
          <Loader modifiers={['LazyImage-loader']} color={'#ffffff'} />
        </div>
      )}
    </picture>
  )
})

LazyImage.propTypes = {
  image: PropTypes.object,
  alt: PropTypes.string,
  sizes: PropTypes.string,
  lazyLoad: PropTypes.object,
  modifiers: PropTypes.array
}

export default withLazyImageContext(LazyImage)
